import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';

// An item opens a mega menu iff it carries layout columns. This MUST match the
// check in FeaturedNavBar (which renders the <MegaMenu> and wires the trigger
// ref); if the two disagree, an item with columns renders a plain <a> here, the
// trigger ref stays null, and Escape-dismissal focus-return breaks. The item's
// URL is NOT part of this check — columns can coexist with a real (non-'#') URL.
const hasMegaMenu = (item) => item.columns?.length > 0;

const FeaturedNavItem = forwardRef(
	(
		{
			item,
			isActive,
			isMegaMenuOpen,
			onClick,
			onMouseEnter,
			onMouseLeave,
			onFocus,
			onBlur
		},
		ref
	) => {
		const activeClass = isActive
			? 'ws-nav-drawer__featured-item--active'
			: '';
		const className = setClassNames([
			'ws-nav-drawer__featured-item',
			activeClass
		]);

		if (hasMegaMenu(item)) {
			return (
				<button
					ref={ref}
					type="button"
					className={className}
					aria-expanded={isMegaMenuOpen}
					aria-controls={`mega-menu-${item.id}`}
					onClick={() => onClick?.(item)}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onFocus={onFocus}
					onBlur={onBlur}
				>
					{item.label}
				</button>
			);
		}

		return (
			<a
				href={item.url}
				target={item.target || '_self'}
				className={className}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onFocus={onFocus}
				onBlur={onBlur}
			>
				{item.label}
			</a>
		);
	}
);

FeaturedNavItem.displayName = 'FeaturedNavItem';

FeaturedNavItem.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string,
		label: PropTypes.string.isRequired,
		url: PropTypes.string,
		target: PropTypes.string,
		columns: PropTypes.arrayOf(
			PropTypes.shape({
				gridUnits: PropTypes.number,
				navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
			})
		)
	}).isRequired,
	isActive: PropTypes.bool.isRequired,
	isMegaMenuOpen: PropTypes.bool,
	onClick: PropTypes.func,
	onMouseEnter: PropTypes.func,
	onMouseLeave: PropTypes.func,
	onFocus: PropTypes.func,
	onBlur: PropTypes.func
};

FeaturedNavItem.defaultProps = {
	isMegaMenuOpen: false,
	onClick: undefined,
	onMouseEnter: undefined,
	onMouseLeave: undefined,
	onFocus: undefined,
	onBlur: undefined
};

export default FeaturedNavItem;
